const YOUTUBE_HOSTS = new Set([
  "youtu.be",
  "youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
]);

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

export function getYoutubeId(raw?: string | null): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, "");
    if (!YOUTUBE_HOSTS.has(host)) return null;

    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0] || "";
      return VIDEO_ID_RE.test(id) ? id : null;
    }

    const v = url.searchParams.get("v");
    if (v && VIDEO_ID_RE.test(v)) return v;

    const parts = url.pathname.split("/").filter(Boolean);
    if (
      parts.length >= 2 &&
      ["embed", "shorts", "live", "v"].includes(parts[0]) &&
      VIDEO_ID_RE.test(parts[1])
    ) {
      return parts[1];
    }
  } catch {
    return null;
  }

  return null;
}

function escapeAttr(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function youtubeIframeHtml(id: string, title = "YouTube video") {
  const src = `https://www.youtube.com/embed/${id}?rel=0`;
  return `<span class="discuss-video-embed"><iframe src="${src}" title="${escapeAttr(
    title
  )}" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen referrerpolicy="strict-origin-when-cross-origin"></iframe></span>`;
}

const isWhitespaceText = (token: any) =>
  token?.type === "text" && !String(token.content || "").trim();

function shouldEmbedYoutubeLink(inner: any[], id: string) {
  const meaningful = inner.filter((token) => !isWhitespaceText(token));
  if (meaningful.length === 0) return true;
  if (meaningful.some((token) => token.type === "image")) {
    return meaningful.every((token) => token.type === "image");
  }
  if (meaningful.length === 1 && meaningful[0].type === "text") {
    const text = String(meaningful[0].content || "").trim();
    return !text || getYoutubeId(text) === id;
  }
  return false;
}

function rewriteInlineChildren(children: any[], state: any) {
  const out: any[] = [];
  for (let i = 0; i < children.length; i += 1) {
    const token = children[i];
    if (token.type !== "link_open") {
      out.push(token);
      continue;
    }

    const id = getYoutubeId(token.attrGet("href"));
    let close = -1;
    for (let j = i + 1; j < children.length; j += 1) {
      if (children[j].type === "link_close") {
        close = j;
        break;
      }
    }

    if (!id || close < 0) {
      out.push(token);
      continue;
    }

    const inner = children.slice(i + 1, close);
    if (!shouldEmbedYoutubeLink(inner, id)) {
      out.push(token);
      continue;
    }

    const image = inner.find((item) => item.type === "image");
    const html = new state.Token("html_inline", "", 0);
    html.content = youtubeIframeHtml(id, image?.content || "YouTube video");
    out.push(html);
    i = close;
  }
  return out;
}

export function youtubeEmbedPlugin(md: any) {
  md.core.ruler.after("inline", "youtube_embed", (state: any) => {
    const tokens = state.tokens;

    for (const token of tokens) {
      if (token.type !== "inline" || !token.children) continue;
      token.children = rewriteInlineChildren(token.children, state);
    }

    for (let i = 0; i < tokens.length - 2; i += 1) {
      if (tokens[i].type !== "paragraph_open") continue;
      if (tokens[i + 1].type !== "inline") continue;
      if (tokens[i + 2].type !== "paragraph_close") continue;

      const kids = (tokens[i + 1].children || []).filter(
        (token: any) => !isWhitespaceText(token)
      );
      if (kids.length !== 1 || kids[0].type !== "text") continue;
      const id = getYoutubeId(kids[0].content);
      if (!id) continue;

      const html = new state.Token("html_block", "", 0);
      html.content = youtubeIframeHtml(id);
      tokens.splice(i, 3, html);
    }
  });
}

export const DISCUSS_HTML_SANITIZE = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: [
    "allow",
    "allowfullscreen",
    "frameborder",
    "loading",
    "referrerpolicy",
    "src",
    "title",
  ],
};
